import { Input, FloatButton, Tooltip } from "antd";
import { styled } from "styled-components";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import SearchAudio from "../../search/SearchAudio";
import SearchAudioWave from "../../search/SearchAudioWave";
import { CloseOutlined } from "@ant-design/icons";
import {
  IConversation,
  IDatabase,
  IMessagePayload,
  chatType,
  dataSourcesInfo,
} from "@dataone/lib";
import { DataSetIcon, PlusIcon, WebIcon } from "@dataone/icons";

interface ChatInputProps {
  stepRef6: MutableRefObject<null>;
  onMessageSend: (message: string) => void;
  selectedOption: number;
  setSelectedOption: (option: number) => void;
  setMessagePayLoad: (message: IMessagePayload) => void;
  messagePayLoad: IMessagePayload;
  setSelectedDatabase?: (database: IDatabase) => void;
  selectedDatabase?: IDatabase;
  handleOpenDrawer?: (e?: React.MouseEvent<HTMLDivElement>) => void;
  setIsOpen?: (isOpen: boolean) => void;
  setSelectedDatasources?: React.Dispatch<
    React.SetStateAction<dataSourcesInfo[]>
  >;
  selectedDatasources?: dataSourcesInfo[];
  selectedConversation?: IConversation;
}

const ChatInput = ({
  stepRef6,
  onMessageSend,
  selectedOption,
  setSelectedOption,
  setSelectedDatabase,
  handleOpenDrawer,
  setIsOpen,
  messagePayLoad,
  setMessagePayLoad,
  setSelectedDatasources,
  selectedDatasources,
  selectedConversation,
}: ChatInputProps) => {
  const [message, setMessage] = useState("");
  const [recordingStatus, setRecordingStatus] = useState<boolean | undefined>(
    false
  );
  const [open, setOpen] = useState(false);
  const [microphonePermissionGranted, setMicrophonePermissionGranted] =
    useState(false);

  useEffect(() => {
    if (recordingStatus) setMessage("");
  }, [recordingStatus, open]);

  const handleMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermissionGranted(true);
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setMicrophonePermissionGranted(false);
      setRecordingStatus(false);
    }
  };

  useEffect(() => {
    if (recordingStatus === true) {
      handleMicrophonePermission();
    }
  }, []);

  const handleMessageSend = (audioMessage?: string) => {
    setIsOpen && setIsOpen(false);
    const msg = (audioMessage || message).trim();
    if (msg !== "") {
      onMessageSend(msg);
      setMessage("");
    }
  };
  const handleChangeOpen = () => {
    setOpen(!open);
    setSelectedDatabase?.({} as IDatabase);
  };

  const handleOptionChange = (
    e: React.MouseEvent<HTMLDivElement>,
    option: number
  ) => {
    setSelectedOption(option);
    handleOpenDrawer!(e);
  };

  const floatButtonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        floatButtonRef.current &&
        !floatButtonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <StyledContainer
      $recordingStatus={!!recordingStatus}
      $disabled={selectedDatasources?.length === 0}
    >
      <StyledAddIcon ref={stepRef6} onClick={(e) => handleOptionChange(e, 1)}>
        <PlusIcon />
      </StyledAddIcon>
      <Input
        placeholder={
          recordingStatus ? "I'm listening" : "Ask me freely, Im here to help"
        }
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={recordingStatus || selectedDatasources?.length === 0}
        suffix={
          <SuffixDiv>
            {recordingStatus && microphonePermissionGranted && (
              <SearchAudioWave />
            )}
            <div
              {...(!recordingStatus &&
                selectedDatasources?.length !== 0 && {
                  onClick: () => setRecordingStatus(true),
                })}
            >
              <SearchAudio
                onWhisperSuccess={(search) => handleMessageSend(search)}
                recordingStatus={!!recordingStatus}
                setRecordingStatus={setRecordingStatus}
                isChatActive={true}
                handleMicrophonePermission={handleMicrophonePermission}
              />
            </div>
          </SuffixDiv>
        }
        onPressEnter={() => handleMessageSend()}
      />
    </StyledContainer>
  );
};

export default ChatInput;

const StyledContainer = styled.div<{
  $recordingStatus: boolean;
  $disabled: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 20px;
  position: relative;

  .ant-input-affix-wrapper {
    border: none;
    border-radius: 30px;
    padding: 10px 25px;
    filter: drop-shadow(2px 2px 5px rgba(73, 116, 246, 0.25));
    background: #fff;
    height: 38px;
    left: 16px;
    margin-right: 30px;

    ::placeholder {
      color: ${({ $recordingStatus }) => $recordingStatus && "#4974F6"};
      font-size: 14px;
      font-weight: 400;
    }

    .ant-input {
      color: rgba(4, 7, 38, 0.75);
      font-size: 14px;
      font-weight: 400;
      letter-spacing: -0.3px;
    }
    .ant-input-outlined: focus {
      box-shadow: none;
    }
  }

  .ant-input-affix-wrapper-disabled,
  .ant-input[disabled] {
    cursor: ${(p) => (p.$disabled ? "not-allowed" : "default")};
  }
  .ant-input-outlined.ant-input-disabled:hover:not([disabled]) {
    background-color: #fff;
  }
  .ant-input-suffix {
    max-width: calc(100% - 120px);
    svg {
      cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
      opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
    }
  }

  .send-icon {
    margin-left: 20px;
    cursor: pointer;
  }
`;
const SuffixDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  img {
    padding: 3px;
    width: 26px;
  }
`;

const StyledFloatingButton = styled.div`
  .ant-float-btn-group {
    position: absolute;
    left: 0px;
    bottom: 3px;
  }
  .ant-float-btn-circle {
    height: 45px;
    margin-bottom: -5px;
  }
  .ant-float-btn {
    width: 45px;
  }
  .ant-float-btn
    .ant-float-btn-body
    .ant-float-btn-content
    .ant-float-btn-icon {
    width: auto !important;
  }
  .ant-float-btn .ant-badge {
    width: auto;
    height: auto;
  }
  .ant-float-btn-default {
    background: linear-gradient(#4974f6, #003fff) !important;
  }
  .ant-float-btn-default .ant-float-btn-body {
    background: linear-gradient(#4974f6, #003fff) !important;
  }
  .ant-float-btn-primary .ant-float-btn-body:hover {
    background-color: transparent;
  }
  .ant-float-btn-default .ant-float-btn-body:hover {
    background-color: transparent;
  }
  :where(.css-dev-only-do-not-override-1m62vyb).ant-float-btn-group-circle
    .ant-float-btn-circle:not(:last-child) {
    margin-bottom: 8px;
  }
  .ant-float-btn-default
    .ant-float-btn-body
    .ant-float-btn-content
    .ant-float-btn-icon {
    color: #fff;
  }
`;
const StyledSwitch = styled.div`
  background-color: #fff;
  border-radius: 30px;
  display: flex;
  .btn {
    display: inline-block;
    position: relative;
    text-align: center;
    transition: background 600ms ease, color 600ms ease;
  }

  input[type="radio"].toggle-chat {
    display: none;
    & + label {
      cursor: pointer;
      &:hover {
        background: none;
        color: #4974f6;
      }
      &:after {
        background: #4974f6;
        border-radius: 30px;
        content: "";
        height: 100%;
        position: absolute;
        top: 0;
        transition: left 200ms cubic-bezier(0.77, 0, 0.175, 1);
        width: 100%;
        z-index: -1;
      }
    }
    &.toggle-chat-left + label {
      border-right: 0;
      &:after {
        left: 100%;
      }
    }
    &.toggle-chat-right + label {
      margin-left: 10px;
      &:after {
        left: -100%;
      }
    }
    &:checked + label {
      cursor: default;
      color: #fff;
      transition: color 200ms;
      z-index: 1;
      &:after {
        left: 0;
      }
    }
  }
  // }
`;
const SwitchChoiceContainer = styled.div<{ $highlited: boolean }>`
  // background-color: ${(p) => (p.$highlited ? "#4974f6" : "#fff")};
  background-color: transparent;
  width: fit-content;
  padding: 6px;
  border-radius: 50%;
  margin: 0px;
  height: fit-content;
  display: flex;
  cursor: pointer;
`;

const StyledDataIcon = styled.div<{ selectedOption: boolean }>`
  display: flex;
  color: ${({ selectedOption }) => (selectedOption ? "#EFF1FD" : "#1A52FB")};
  font-size: 25px;
`;
const StyledAddIcon = styled.div`
  svg {
    color: #4974f6;
    font-size: 25px;
    cursor: pointer;
    border-radius: 50%;
    border: 1px solid #4974f6;
    &:hover {
      background-color: #4974f6;
      color: #fff;
    }
  }
`;
